import {
  GET_REPORT_ANALYTICS_CONFIG_DETAILS,
  ReportAnalyticsConfigPayload,
  FETCH_PERFORMANCE_METRICS,
  FetchPerformanceMetricsAction,
  FetchPerformanceMetricsPayload,
  FETCH_KPIS_PIE_CHART,
  FetchPieChartDataAction,
  FetchPieChartDataPayload,
  SET_PERFORMANCE_REPORT_DATE_RANGE,
  SET_PERFORMANCE_REPORT_STORE_ID,
  SetPerformanceReportDateRangeAction,
  SetPerformanceReportDateRangeActionPayload,
  SetPerformanceReportStoreIdAction,
  SetPerformanceReportStoreIdActionPayload,
  LoadReportAnalyticsConfigAction,
  MetricCategory,
  PieChartType,
  FetchKPIProductsAction,
  FetchKPIProductsPayload,
  FETCH_KPI_PRODUCTS,
  SET_KPI_PRODUCTS_LOADING,
  FETCH_ORDERS_BY_LOCATIONS,
  RESET_LOCATION_TREE_STATE,
  FetchLocationTreeDataAction,
  FetchLocationTreeDataPayload,
  ResetLocationTreeStateAction,
  FETCH_KPI_ANALYSIS,
  FetchKPIAnalysisAction,
  FetchKPIAnalysisPayload,
  FetchDateYearMetricsAction,
  FETCH_DATE_YEAR_METRICS,
  FetchDateYearMetricsPayload,
  UPDATE_KPI_PRODUCTS_DETAILS,
  UpdateKPIProductsDetailsAction,
  UpdateKPIProductsDetailsPayload,
  LOAD_KPI_STORE_LIST,
  LoadKPIStoreListAction,
} from "./PerformanceReportTypes";
import {
  TRAFFIC_METRICS_PAYLOAD,
  PIE_CHART_PAYLOADS,
  CONVERSION_METRICS_PAYLOAD,
  PRODUCT_METRICS_PAYLOAD,
  REVENUE_METRICS_PAYLOAD,
} from "utils/PerformanceReportUtils";

export const setPerformanceReportStoreId = (
  payload: SetPerformanceReportStoreIdActionPayload,
): SetPerformanceReportStoreIdAction => {
  return {
    payload,
    type: SET_PERFORMANCE_REPORT_STORE_ID,
  };
};

export const setPerformanceReportDateRange = (
  payload: SetPerformanceReportDateRangeActionPayload,
): SetPerformanceReportDateRangeAction => {
  return { payload, type: SET_PERFORMANCE_REPORT_DATE_RANGE };
};

export const fetchReportAnalyticsConfig = (
  payload: ReportAnalyticsConfigPayload,
): LoadReportAnalyticsConfigAction => {
  return {
    type: GET_REPORT_ANALYTICS_CONFIG_DETAILS.REQUEST,
    payload,
  };
};

export const fetchPerformanceMetrics = (
  payload: FetchPerformanceMetricsPayload,
): FetchPerformanceMetricsAction => {
  return { payload, type: FETCH_PERFORMANCE_METRICS.REQUEST };
};

export const fetchTrafficMetrics = (
  storeId: string,
  dateRange: { startDate: string; endDate: string },
  comparisonDateRange: { startDate: string; endDate: string },
  metrics: { id: string; name: string }[] = TRAFFIC_METRICS_PAYLOAD,
): FetchPerformanceMetricsAction => {
  return fetchPerformanceMetrics({
    storeId,
    category: MetricCategory.TRAFFIC,
    dateRange,
    comparisonDateRange,
    metrics,
  });
};

export const fetchPieChartData = (
  payload: FetchPieChartDataPayload,
): FetchPieChartDataAction => {
  return { payload, type: FETCH_KPIS_PIE_CHART.REQUEST };
};

export const fetchTrafficBySource = (
  storeId: string,
  dateRange: { startDate: string; endDate: string },
): FetchPieChartDataAction => {
  return fetchPieChartData({
    storeId,
    chartType: PieChartType.TRAFFIC_BY_SOURCE,
    dateRange,
    ...PIE_CHART_PAYLOADS.TRAFFIC_BY_SOURCE,
  });
};

export const fetchTrafficByDevice = (
  storeId: string,
  dateRange: { startDate: string; endDate: string },
): FetchPieChartDataAction => {
  return fetchPieChartData({
    storeId,
    chartType: PieChartType.TRAFFIC_BY_DEVICE,
    dateRange,
    ...PIE_CHART_PAYLOADS.TRAFFIC_BY_DEVICE,
  });
};

export const fetchConversionMetrics = (
  storeId: string,
  dateRange: { startDate: string; endDate: string },
  comparisonDateRange: { startDate: string; endDate: string },
  metrics: { id: string; name: string }[] = CONVERSION_METRICS_PAYLOAD,
): FetchPerformanceMetricsAction => {
  return fetchPerformanceMetrics({
    storeId,
    category: MetricCategory.CONVERSION,
    dateRange,
    comparisonDateRange,
    metrics,
  });
};

export const fetchOrdersByChannel = (
  storeId: string,
  dateRange: { startDate: string; endDate: string },
): FetchPieChartDataAction => {
  return fetchPieChartData({
    storeId,
    chartType: PieChartType.ORDERS_BY_SOURCE,
    dateRange,
    ...PIE_CHART_PAYLOADS.ORDERS_BY_SOURCE,
  });
};

export const fetchOrdersByDevice = (
  storeId: string,
  dateRange: { startDate: string; endDate: string },
): FetchPieChartDataAction => {
  return fetchPieChartData({
    storeId,
    chartType: PieChartType.ORDERS_BY_DEVICE,
    dateRange,
    ...PIE_CHART_PAYLOADS.ORDERS_BY_DEVICE,
  });
};

export const fetchProductMetrics = (
  storeId: string,
  dateRange: { startDate: string; endDate: string },
  comparisonDateRange: { startDate: string; endDate: string },
  metrics: { id: string; name: string }[] = PRODUCT_METRICS_PAYLOAD,
): FetchPerformanceMetricsAction => {
  return fetchPerformanceMetrics({
    storeId,
    category: MetricCategory.PRODUCT,
    dateRange,
    comparisonDateRange,
    metrics,
  });
};

export const fetchRevenueMetrics = (
  storeId: string,
  dateRange: { startDate: string; endDate: string },
  comparisonDateRange: { startDate: string; endDate: string },
  metrics: { id: string; name: string }[] = REVENUE_METRICS_PAYLOAD,
): FetchPerformanceMetricsAction => {
  return fetchPerformanceMetrics({
    storeId,
    category: MetricCategory.REVENUE,
    dateRange,
    comparisonDateRange,
    metrics,
  });
};

export const fetchKPIProducts = (
  payload: FetchKPIProductsPayload,
): FetchKPIProductsAction => {
  return { payload, type: FETCH_KPI_PRODUCTS.REQUEST };
};

export const setKPIProductsLoadingState = () => {
  return { type: SET_KPI_PRODUCTS_LOADING };
};

export const fetchLocationTreeData = (
  payload: FetchLocationTreeDataPayload,
): FetchLocationTreeDataAction => ({
  type: FETCH_ORDERS_BY_LOCATIONS.REQUEST,
  payload,
});

export const resetLocationTreeState = (): ResetLocationTreeStateAction => ({
  type: RESET_LOCATION_TREE_STATE,
});

export const fetchKPIAnalysis = (
  payload: FetchKPIAnalysisPayload,
): FetchKPIAnalysisAction => ({
  type: FETCH_KPI_ANALYSIS.REQUEST,
  payload,
});

export const fetchDateYearMetrics = (
  payload: FetchDateYearMetricsPayload,
): FetchDateYearMetricsAction => ({
  type: FETCH_DATE_YEAR_METRICS.REQUEST,
  payload: {
    ...payload,
    limit: payload.limit ?? 14,
  },
});

export const updateKPIProductsDetails = (
  payload: UpdateKPIProductsDetailsPayload,
): UpdateKPIProductsDetailsAction => ({
  type: UPDATE_KPI_PRODUCTS_DETAILS,
  payload,
});

export const loadKPIStoreList = (): LoadKPIStoreListAction => ({
  type: LOAD_KPI_STORE_LIST,
});
